import { Heart, MessageCircle, Send, Bookmark } from "lucide-react";

/**
 * Realistic IG/FB Feed + Story preview mockup.
 * Props:
 *  - platform: "instagram" | "facebook"
 *  - formatType: "post" | "reel" | "story"  (derived from backend format_type lowercased)
 *  - caption, hashtags, image, companyName
 */
export default function ContentPreview({
  platform = "instagram",
  formatType = "post",
  caption,
  hashtags = [],
  image,
  companyName,
}) {
  const isIG = platform === "instagram";
  const isStory = formatType === "story" || formatType === "reel";
  const username = companyName?.toLowerCase().replace(/\s+/g, "") || "studio";
  const now = new Date();
  const timeLabel = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  // ========== STORY / REEL MOCKUP (9:16 full-bleed) ==========
  if (isStory) {
    return (
      <div
        className="relative rounded-2xl overflow-hidden bg-black max-w-[260px] mx-auto shadow-2xl"
        style={{ aspectRatio: "9 / 16" }}
        data-testid="content-preview"
      >
        {image ? (
          <img src={image} alt="Story" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-sm">
            9:16 Bild-Vorschau
          </div>
        )}

        {/* Progress bars top */}
        <div className="absolute top-2 left-2 right-2 flex gap-1">
          <div className="flex-1 h-0.5 bg-white/90 rounded-full" />
          <div className="flex-1 h-0.5 bg-white/30 rounded-full" />
          <div className="flex-1 h-0.5 bg-white/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="absolute top-5 left-3 right-3 flex items-center gap-2 text-white drop-shadow-lg">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#fdcb52] via-[#E1306C] to-[#833AB4] p-[2px]">
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-[10px] font-bold">
              {companyName?.[0] || "C"}
            </div>
          </div>
          <span className="text-xs font-semibold">{username}</span>
          <span className="text-[10px] text-white/70">{timeLabel}</span>
        </div>

        {/* Caption overlay bottom */}
        {caption && (
          <div className="absolute bottom-16 left-3 right-3 text-white drop-shadow-lg">
            <p className="text-sm font-medium leading-snug line-clamp-4">{caption}</p>
          </div>
        )}

        {/* Reply bar mockup */}
        <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
          <div className="flex-1 h-9 rounded-full border border-white/40 flex items-center px-4 text-xs text-white/70">
            Antworten…
          </div>
          <Heart className="w-6 h-6 text-white" />
          <Send className="w-6 h-6 text-white" />
        </div>

        {/* Format badge */}
        <div className="absolute top-5 right-3 text-[9px] bg-black/40 backdrop-blur-sm text-white px-1.5 py-0.5 rounded font-mono">
          {formatType.toUpperCase()}
        </div>
      </div>
    );
  }

  // ========== INSTAGRAM POST (1:1) ==========
  if (isIG) {
    return (
      <div
        className="rounded-xl overflow-hidden border border-[var(--studio-border)] bg-white max-w-[360px]"
        data-testid="content-preview"
      >
        <div className="flex items-center gap-2 p-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#fdcb52] via-[#E1306C] to-[#833AB4] p-[2px]">
            <div className="w-full h-full rounded-full bg-white flex items-center justify-center text-[11px] font-bold">
              {companyName?.[0] || "C"}
            </div>
          </div>
          <span className="text-xs font-semibold flex-1">{username}</span>
          <span className="text-gray-400 text-xs">•••</span>
        </div>
        <div className="bg-gray-200 relative" style={{ aspectRatio: "4 / 5" }}>
          {image ? (
            <img src={image} alt="Post" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
              4:5 Bild-Vorschau
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-3 pt-3">
          <div className="flex items-center gap-3">
            <Heart className="w-6 h-6" strokeWidth={1.5} />
            <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
            <Send className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <Bookmark className="w-6 h-6" strokeWidth={1.5} />
        </div>
        <div className="px-3 pt-2">
          <p className="text-xs font-semibold">{Math.floor(120 + Math.random() * 800)} „Gefällt mir"-Angaben</p>
        </div>
        <div className="px-3 pt-1 pb-3">
          <p className="text-xs">
            <span className="font-semibold mr-1">{username}</span>
            {caption || "Caption wird hier angezeigt…"}
          </p>
          {hashtags.length > 0 && (
            <p className="text-xs text-blue-600 mt-1">{hashtags.slice(0, 5).join(" ")}</p>
          )}
          <p className="text-[10px] text-gray-400 uppercase mt-2">vor {Math.floor(Math.random() * 12) + 1} stunden</p>
        </div>
      </div>
    );
  }

  // ========== FACEBOOK POST ==========
  return (
    <div
      className="rounded-lg overflow-hidden border border-[var(--studio-border)] bg-white max-w-[360px]"
      data-testid="content-preview"
    >
      <div className="flex items-center gap-2 p-3">
        <div className="w-9 h-9 rounded-full bg-[#1877F2] flex items-center justify-center text-white text-sm font-bold">
          {companyName?.[0] || "C"}
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold">{companyName || "InvictaFlow"}</p>
          <p className="text-[10px] text-gray-500">Gerade eben · 🌐</p>
        </div>
      </div>
      {caption && <p className="text-sm px-3 pb-2">{caption}</p>}
      <div className="bg-gray-200 relative" style={{ aspectRatio: "1.91 / 1" }}>
        {image ? (
          <img src={image} alt="FB Post" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
            Bild-Vorschau
          </div>
        )}
      </div>
      <div className="flex items-center gap-6 px-3 py-2 border-t border-gray-100 text-xs text-gray-600">
        <span>👍 Gefällt mir</span>
        <span>💬 Kommentieren</span>
        <span>↗ Teilen</span>
      </div>
    </div>
  );
}
